import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';

/**
 * Where the Doctors and the Patients lists stand, kept in the address. The
 * request of 6 October 2026 (fifth), section 1: pressing a count narrows the
 * list, and coming Back from a record has to find the list as it was left.
 *
 * So what was searched for, which count the list is narrowed to, whether the
 * possible duplicates are listed, the order and the page are search params of
 * the list's own address: `/doctors?q=roe&show=withActiveCases&page=2`.
 * Every change REPLACES the entry rather than adding one, as the tabs of
 * Archive and Users do, so Back still leaves the list in one step, and Back
 * from a profile, which is a new entry, returns to the list exactly as it was.
 * A link to the list can be sent to somebody else for the same reason.
 *
 * Anything that makes it another list (a search, a count, the duplicates, an
 * order) starts it again at its first page, in the same write.
 */

const Q = 'q';
const SHOW = 'show';
const DUPLICATES = 'duplicates';
const SORT = 'sort';
const PAGE = 'page';

export interface ListAddress {
  /** The search box, letter for letter as it is typed. */
  text: string;
  setText: (text: string) => void;
  /** What is searched for: the box once the typing has paused, trimmed. */
  q: string;
  /** The word of the count the list is narrowed to, as the address has it, or null. */
  show: string | null;
  /** Narrows the list to a count, or widens it again with null. Ends the possible duplicates. */
  setShow: (show: string | null) => void;
  duplicates: boolean;
  /** Lists the possible duplicates, or everybody again. Ends any narrowing by a count. */
  setDuplicates: (on: boolean) => void;
  /** The order the address asks for, or null when it asks for none. */
  sort: string | null;
  setSort: (sort: string | null) => void;
  /** The page, from 1. */
  page: number;
  setPage: (page: number) => void;
}

/** A page number as an address carries one: a whole number from 1, and 1 for anything else. */
function pageOf(text: string | null): number {
  const page = Number(text ?? '');
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

export function useListAddress(): ListAddress {
  const [params, setParams] = useSearchParams();
  // The latest setter, for the writes made from an effect: its identity is the router's business.
  const write = useRef(setParams);
  write.current = setParams;

  /** One write for everything that changes together: a null takes the key away. */
  const patch = useCallback((changes: Record<string, string | null>) => {
    write.current(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }, []);

  /*
   * The search box shows every keystroke at once and the address follows when
   * the typing pauses: a write for each letter would be a history call for
   * each letter. What the address holds is remembered, so a change that did
   * not come from the box (the menu's link to the bare list, a link somebody
   * sent) is told from the box's own writes and put into the box.
   */
  const inAddress = params.get(Q) ?? '';
  const [text, setText] = useState(inAddress);
  const q = useDebouncedValue(text).trim();
  const known = useRef(inAddress);

  useEffect(() => {
    if (inAddress === known.current) return;
    known.current = inAddress;
    setText(inAddress);
  }, [inAddress]);

  useEffect(() => {
    if (q === known.current.trim()) return;
    known.current = q;
    patch({ [Q]: q, [PAGE]: null });
  }, [q, patch]);

  const show = params.get(SHOW);
  const duplicates = params.get(DUPLICATES) === '1';
  const sort = params.get(SORT);
  const page = pageOf(params.get(PAGE));

  return useMemo(
    () => ({
      text,
      setText,
      q,
      show,
      setShow: (next) => patch({ [SHOW]: next, [DUPLICATES]: null, [PAGE]: null }),
      duplicates,
      setDuplicates: (on) => patch({ [DUPLICATES]: on ? '1' : null, [SHOW]: null, [PAGE]: null }),
      sort,
      setSort: (next) => patch({ [SORT]: next, [PAGE]: null }),
      page,
      setPage: (next) => patch({ [PAGE]: next <= 1 ? null : String(next) }),
    }),
    [text, q, show, duplicates, sort, page, patch],
  );
}
